import React from 'react';
import i18n from '../../../../i18n';
import BlazeInputDropdown, { BlazeDropdownItem } from '../../../ui/dropdown/BlazeDropdown/BlazeInputDropdown';
import _ from 'lodash';
import BzmIcon from '../../../icons/BzmIcon';
import Row from 'react-bootstrap/Row';
import Col from 'react-bootstrap/Col';

export interface CdataSelectedCounts {
  items: { [cdataItemId: string]: number };
  total: number;
}

interface CDataDropdownProps {
  /** List of items for the dropdown */
  dropdownItems: BlazeDropdownItem[];

  /** Currently active item */
  selectedItem: BlazeDropdownItem;

  /** Item in dropdown selected action*/
  onSelect: (cdataSectionItem: BlazeDropdownItem) => void;

  /** Map with information about selected nodes within each CData block. */
  selectedCdataNodesCounts: CdataSelectedCounts;
}

const defaultCdataNode: BlazeDropdownItem = { name: i18n.t('transactions:editor.cdataDropdown.noItems'), id: -1 };

const tickIcon = (
  <span className={'position-relative mr-2'} style={{ top: 1 }}>
    <BzmIcon variant={'icon-bzm-checkmark'} />
  </span>
);

/**
 * Helper which creates text "x node(s) selected (in total)"
 * @param count the number to render as count
 * @param isTotal if true, "in total" is appended
 */
const getSelectedCountSpan = (count: number, isTotal: boolean) => {
  let nodeText = i18n.t('transactions:editor.cdataDropdown.node_multiple');
  let selectedText = i18n.t('transactions:editor.cdataDropdown.selectedCounts');

  if (_.isNil(count)) {
    count = 0;
  }

  //Singular form
  if (count === 1) {
    nodeText = i18n.t('transactions:editor.cdataDropdown.node_one');
  }

  if (isTotal) {
    selectedText = i18n.t('transactions:editor.cdataDropdown.selectedCountsTotal');
  }

  return <span>{`${count} ${nodeText} ${selectedText}`}</span>;
};

/**
 * Component displays list CData blocks in the dropdown.
 * @constructor
 */
const CDataDropdown = (props: CDataDropdownProps) => {
  const { selectedCdataNodesCounts, onSelect, selectedItem, dropdownItems } = props;
  const getDropdownItems = () => {
    return _.map(props.dropdownItems, item => {
      const count = _.get(props.selectedCdataNodesCounts.items, item.name);
      return { ...item, htmlContent: <CdataDropdownItem count={count} text={item.name} /> };
    });
  };

  return (
    <>
      <label className={'font-size-lg font-weight-bold mt-2 mb-2'}>
        {i18n.t('transactions:editor.cdataDropdown.title')}
      </label>
      <BlazeInputDropdown
        isSearchable={false}
        id={'cdata-xpath-node-picker'}
        minDropdownMenuWidth={'900px'}
        items={getDropdownItems()}
        selected={selectedItem}
        defaultOption={defaultCdataNode}
        isDefaultOptionSelectable={false}
        noItemsPlaceholder={i18n.t('transactions:editor.cdataDropdown.noItems')}
        onClick={onSelect}
      />
      <small className={'text-muted mt-1'}>
        {tickIcon}
        {getSelectedCountSpan(selectedCdataNodesCounts.total, true)}
      </small>
    </>
  );
};

interface CdataDropdownItemProps {
  text: string;
  count: number;
}

const CdataDropdownItem = (props: CdataDropdownItemProps) => {
  return (
    <Row>
      <Col md={8}>{props.text}</Col>
      <Col md={4} className={'text-right'}>
        <span className={'text-muted'}>
          {tickIcon}
          {getSelectedCountSpan(props.count, false)}
        </span>
      </Col>
    </Row>
  );
};

export default CDataDropdown;
